import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    Box,
    Grid,
    CircularProgress,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useFeeReceipt, FeeParticular } from "../../hooks/useFeeReceipt";
import { StudentAccountsResponseDto } from "../../hooks/useStudentAccounts";
import { PaymentType, FeeType, FEE_TYPES } from "../../types/payment";
import { useError } from "../../contexts/ErrorContext";

interface FeePaymentModalProps {
    open: boolean;
    handleClose: () => void;
    account: StudentAccountsResponseDto | null;
}

// Internal interface that includes feeType for UI logic
interface FeeParticularInternal extends FeeParticular {
    feeType: FeeType;
}

const FeePaymentModal: React.FC<FeePaymentModalProps> = ({
    open,
    handleClose,
    account,
}) => {
    const { t } = useTranslation();
    const { showError, showSuccess } = useError();
    const { payFeesMutation } = useFeeReceipt();

    const [paymentType, setPaymentType] = useState<PaymentType>("CASH");
    const [remark, setRemark] = useState("");
    const [particulars, setParticulars] = useState<FeeParticularInternal[]>([
        { description: "", amount: 0, feeType: "TUITION_FEES" }
    ]);

    // Calculate total amount from particulars
    const totalAmount = useMemo(() => {
        return particulars.reduce((sum, p) => sum + (p.amount || 0), 0);
    }, [particulars]);

    useEffect(() => {
        if (open && account) {
            setParticulars([
                {
                    description: t("tuition_fees"),
                    amount: account.dueAmount,
                    feeType: "TUITION_FEES"
                }
            ]);
            setPaymentType("CASH");
            setRemark("");
        }
    }, [open, account, t]);

    const handleAddParticular = () => {
        setParticulars([
            ...particulars,
            { description: "", amount: 0, feeType: "OTHER" }
        ]);
    };

    const handleRemoveParticular = (index: number) => {
        if (particulars.length > 1) {
            const newParticulars = [...particulars];
            newParticulars.splice(index, 1);
            setParticulars(newParticulars);
        }
    };

    const handleParticularChange = (index: number, field: keyof FeeParticularInternal, value: any) => {
        const newParticulars = [...particulars];
        newParticulars[index] = { ...newParticulars[index], [field]: value };

        if (field === "feeType") {
            const translationKey = value === "OTHER" ? "other_fees" : value.toLowerCase();
            newParticulars[index].description = t(translationKey);
        }

        setParticulars(newParticulars);
    };

    const handleSubmit = () => {
        if (particulars.some(p => !p.amount || p.amount <= 0)) {
            showError(t("please_enter_valid_amounts_for_all_particulars"));
            return;
        }

        if (!account) return;

        // Strip feeType before sending to backend
        const particularsToSubmit: FeeParticular[] = particulars.map(({ feeType, ...rest }) => rest);

        payFeesMutation.mutate(
            {
                studentAccountNo: account.studentAccountNumber,
                particulars: particularsToSubmit,
                paymentType: paymentType,
                remark: remark,
            },
            {
                onSuccess: () => {
                    showSuccess(`${t("payment_success")} ${account.student?.studentName}`);
                    handleClose();
                },
                onError: (error: any) => {
                    showError(error?.response?.data?.message || t("payment_failed"));
                },
            }
        );
    };

    if (!account) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 5, boxShadow: "0 24px 48px rgba(0,0,0,0.12)" }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#fff",
                    pb: 1,
                    pt: 3,
                    px: 4
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ bgcolor: "primary.main", p: 1, borderRadius: 2, display: "flex" }}>
                        <PaymentIcon sx={{ color: "#fff" }} />
                    </Box>
                    <Typography variant="h5" fontWeight="800" color="text.primary">
                        {t("pay_fees")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{ bgcolor: "#F8F9FA", "&:hover": { bgcolor: "#E9ECEF" } }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 1, px: 4, pb: 2 }}>
                <Grid container spacing={4}>
                    {/* Student Info Section */}
                    <Grid item xs={12} md={4.5}>
                        <Box sx={{
                            p: 3,
                            bgcolor: "#F8F9FA",
                            borderRadius: 4,
                            border: "1px solid #EDEDED",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2.5
                        }}>
                            <Typography variant="overline" color="text.secondary" fontWeight="700" letterSpacing={1.2}>
                                {t("student_details")}
                            </Typography>

                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{t("student_name")}</Typography>
                                <Typography variant="body1" fontWeight="700" color="text.primary">{account.student?.studentName}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{t("father_name")}</Typography>
                                <Typography variant="body1" fontWeight="700" color="text.primary">{account.student?.fatherName?.toUpperCase()}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{t("account_number")}</Typography>
                                <Typography variant="body2" fontWeight="600" sx={{ wordBreak: "break-all", opacity: 0.8 }}>{account.studentAccountNumber}</Typography>
                            </Box>

                            <Divider sx={{ my: 1, borderStyle: "dashed" }} />

                            <Box sx={{ mt: "auto" }}>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{t("due_amount")}</Typography>
                                <Typography variant="h4" color="error.main" fontWeight="900">
                                    ₹{account.dueAmount.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={7.5}>
                        <Grid container spacing={3}>
                            {/* Payment Settings Row */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined" size="medium">
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.2, display: "block", fontWeight: 700, ml: 0.5 }}>
                                        {t("payment_type")}
                                    </Typography>
                                    <Select
                                        value={paymentType}
                                        onChange={(e) => setPaymentType(e.target.value as PaymentType)}
                                        sx={{
                                            borderRadius: 4,
                                            bgcolor: "#F8F9FA",
                                            "& .MuiOutlinedInput-notchedOutline": { border: "1px solid #F0F0F0" },
                                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                                            fontWeight: 700
                                        }}
                                    >
                                        <MenuItem value="CASH">{t("CASH")}</MenuItem>
                                        <MenuItem value="ONLINE">{t("ONLINE")}</MenuItem>
                                        <MenuItem value="CHEQUE">{t("CHEQUE")}</MenuItem>
                                        <MenuItem value="OTHER">{t("OTHER")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.2, display: "block", fontWeight: 700, ml: 0.5 }}>
                                    {t("total_paying_amount")}
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={totalAmount}
                                    InputProps={{
                                        readOnly: true,
                                        startAdornment: <Typography sx={{ mr: 1, fontWeight: "900", color: "#666" }}>₹</Typography>,
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 4,
                                            bgcolor: "#F8F9FA",
                                            "& fieldset": { border: "1px solid #F0F0F0" },
                                            fontWeight: "900",
                                            fontSize: "18px"
                                        }
                                    }}
                                />
                            </Grid>

                            {/* Particulars Title & Add Button Row */}
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, mt: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="800" color="text.primary">
                                        {t("payment_particulars")}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddParticular}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: 2.5,
                                            px: 2.5,
                                            py: 0.8,
                                            boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)"
                                        }}
                                    >
                                        {t("add_item")}
                                    </Button>
                                </Box>

                                {/* Table Header Row */}
                                <Box sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 140px 48px",
                                    gap: 2,
                                    px: 2,
                                    mb: 1
                                }}>
                                    <Typography variant="caption" fontWeight="800" color="text.secondary" sx={{ ml: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        {t("type")}
                                    </Typography>
                                    <Typography variant="caption" fontWeight="800" color="text.secondary" sx={{ ml: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        {t("amount")}
                                    </Typography>
                                    <Box /> {/* Spacer for delete icon */}
                                </Box>

                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                    maxHeight: "320px",
                                    overflowY: "auto",
                                    pr: 1
                                }}>
                                    {particulars.map((p, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 140px 48px",
                                                gap: 2,
                                                alignItems: "center",
                                                p: 1.5,
                                                borderRadius: 4,
                                                border: "1px solid #F0F0F0",
                                                bgcolor: "#fff",
                                                transition: "all 0.2s",
                                                "&:hover": { borderColor: "primary.main", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }
                                            }}
                                        >
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={p.feeType}
                                                onChange={(e) => handleParticularChange(index, "feeType", e.target.value)}
                                                sx={{
                                                    borderRadius: 3,
                                                    bgcolor: "#F8F9FA",
                                                    "& .MuiOutlinedInput-notchedOutline": { border: "1px solid #F0F0F0" },
                                                    fontWeight: 700
                                                }}
                                            >
                                                {FEE_TYPES.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {t(type.toLowerCase())}
                                                    </MenuItem>
                                                ))}
                                            </Select>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={p.amount || ""}
                                                onChange={(e) => handleParticularChange(index, "amount", parseFloat(e.target.value) || 0)}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: 3,
                                                        bgcolor: "#fff",
                                                        "& fieldset": { borderColor: "#F0F0F0" },
                                                        fontWeight: 800
                                                    }
                                                }}
                                                InputProps={{
                                                    startAdornment: <Typography variant="caption" sx={{ mr: 0.5, fontWeight: 900, color: "#666" }}>₹</Typography>
                                                }}
                                            />

                                            <IconButton
                                                size="small"
                                                color="error"
                                                disabled={particulars.length === 1}
                                                onClick={() => handleRemoveParticular(index)}
                                                sx={{
                                                    bgcolor: "#fff5f5",
                                                    "&:hover": { bgcolor: "#ffeaea" }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>

                            {/* Remark Row */}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.2, display: "block", fontWeight: 700, ml: 0.5 }}>
                                    {t("remark")}
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder={t("enter_remark_optional")}
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    multiline
                                    rows={2}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 4,
                                            bgcolor: "#F8F9FA",
                                            "& fieldset": { border: "1px solid #F0F0F0" },
                                            fontWeight: 500
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 4, pt: 2, display: "flex", gap: 2 }}>
                <Button
                    fullWidth
                    onClick={handleClose}
                    sx={{
                        color: "text.secondary",
                        fontWeight: "700",
                        textTransform: "none",
                        borderRadius: 3.5,
                        py: 1.5,
                        bgcolor: "#F5F5F5",
                        "&:hover": { bgcolor: "#EEEEEE" }
                    }}
                >
                    {t("cancel")}
                </Button>
                <Button
                    fullWidth
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={payFeesMutation.isLoading || totalAmount <= 0}
                    sx={{
                        fontWeight: "800",
                        textTransform: "none",
                        borderRadius: 3.5,
                        py: 1.5,
                        backgroundColor: "#2196F3",
                        boxShadow: "0 8px 16px rgba(33, 150, 243, 0.24)",
                        "&:hover": {
                            backgroundColor: "#1976D2",
                            boxShadow: "0 12px 24px rgba(33, 150, 243, 0.32)",
                        }
                    }}
                >
                    {payFeesMutation.isLoading ? <CircularProgress size={24} color="inherit" /> : t("confirm_payment")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeePaymentModal;
